import { useQuery } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { pedir } from '../api/cliente';
import { Boton } from '../componentes/ui/basicos';
import { Cargando, Problema } from '../componentes/ui/estados';

interface Movimiento {
  readonly id: string;
  readonly tipo: string;
  readonly importe: string;
  readonly vencimiento: string | null;
  readonly concepto: string | null;
  readonly ocurridoEn: string;
}

interface EstadoDeCuentaDelTercero {
  readonly tercero: { readonly razonSocial: string; readonly identificacion: string | null };
  readonly saldo: string;
  readonly movimientos: readonly Movimiento[];
}

function conDosDecimales(importe: string): string {
  const [entera, fraccion = ''] = importe.split('.');
  return `${entera ?? '0'}.${fraccion.padEnd(2, '0').slice(0, 2)}`;
}

/**
 * Cada renglón con el saldo que dejó.
 *
 * Se suma **en centavos enteros**: es plata, y acumular con coma flotante deja el último renglón
 * con un centavo que nadie puede explicar y que discute el cliente.
 */
function conAcumulado(movimientos: readonly Movimiento[]): (Movimiento & { acumulado: string })[] {
  return movimientos.reduce<{ total: number; filas: (Movimiento & { acumulado: string })[] }>(
    (estado, movimiento) => {
      const [entera = '0', fraccion = ''] = movimiento.importe.split('.');
      const total =
        estado.total + Number(entera) * 100 + Number(fraccion.padEnd(2, '0').slice(0, 2));
      const absoluto = Math.abs(total);
      const acumulado = `${total < 0 ? '-' : ''}${String(Math.floor(absoluto / 100))}.${String(
        absoluto % 100,
      ).padStart(2, '0')}`;
      return { total, filas: [...estado.filas, { ...movimiento, acumulado }] };
    },
    { total: 0, filas: [] },
  ).filas;
}

/**
 * El estado de cuenta, para mirar e imprimir.
 *
 * Es una pantalla propia y no un diálogo porque **se imprime y se le da al cliente**: un diálogo
 * imprime la pantalla que tiene detrás, con el menú y la tabla de todos los demás deudores.
 *
 * Lleva el saldo acumulado renglón por renglón, que es lo que hace que el papel se pueda
 * discutir: sin esa columna, quien recibe el resumen tiene que sumar a mano para encontrar dónde
 * dejaron de coincidir las cuentas.
 *
 * El plano viaja en la dirección —`?plano=proveedor`— porque el mismo tercero puede tener las dos
 * cuentas y son dos papeles distintos.
 */
export function EstadoDeCuenta(): React.ReactElement {
  const { t } = useTranslation();
  const partes = globalThis.location.pathname.split('/');
  const terceroId = partes[partes.length - 1] ?? '';
  const plano = new URLSearchParams(globalThis.location.search).get('plano') ?? 'cliente';

  const consulta = useQuery({
    queryKey: ['estado-de-cuenta', terceroId, plano],
    queryFn: () =>
      pedir<EstadoDeCuentaDelTercero>(`/cuentas-corrientes/${terceroId}?plano=${plano}`),
  });

  if (consulta.isPending) return <Cargando />;
  if (consulta.isError) {
    return (
      <Problema
        error={consulta.error}
        reintentar={() => {
          void consulta.refetch();
        }}
      />
    );
  }

  const renglones = conAcumulado(consulta.data.movimientos);

  return (
    <div className="mx-auto max-w-3xl">
      <div className="mb-4 flex items-center justify-between gap-2 print:hidden">
        <h1 className="text-lg font-semibold">{t('cuentas.estadoDeCuenta')}</h1>
        <Boton
          onClick={() => {
            globalThis.print();
          }}
        >
          {t('acciones.imprimir')}
        </Boton>
      </div>

      <article className="rounded-marco border border-borde p-6 print:border-0 print:p-0">
        <header className="mb-4">
          <p className="text-base font-semibold">{consulta.data.tercero.razonSocial}</p>
          {consulta.data.tercero.identificacion === null ? null : (
            <p className="numero text-sm">{consulta.data.tercero.identificacion}</p>
          )}
          <p className="text-sm text-texto-tenue">
            {t(plano === 'proveedor' ? 'cuentas.comoProveedor' : 'cuentas.comoCliente')} ·{' '}
            {new Date().toLocaleDateString()}
          </p>
        </header>

        <table className="w-full text-sm">
          <thead>
            <tr className="border-b border-borde text-left">
              <th className="py-1">{t('cuentas.fecha')}</th>
              <th className="py-1">{t('cuentas.concepto')}</th>
              <th className="py-1 text-right">{t('cuentas.importe')}</th>
              <th className="py-1 text-right">{t('cuentas.acumulado')}</th>
            </tr>
          </thead>
          <tbody>
            {renglones.map((renglon) => (
              <tr key={renglon.id} className="border-b border-borde/40">
                <td className="numero py-1">{new Date(renglon.ocurridoEn).toLocaleDateString()}</td>
                <td className="py-1">
                  {renglon.concepto ?? t(`cuentas.tipos.${renglon.tipo}`, renglon.tipo)}
                  {renglon.vencimiento === null ? null : (
                    <span className="block text-xs text-texto-tenue">
                      {t('cuentas.vence')}: {renglon.vencimiento}
                    </span>
                  )}
                </td>
                <td className="numero py-1 text-right">{conDosDecimales(renglon.importe)}</td>
                <td className="numero py-1 text-right">{renglon.acumulado}</td>
              </tr>
            ))}
          </tbody>
        </table>

        <p className="mt-4 text-right text-base font-semibold">
          {t('cuentas.saldo')}:{' '}
          <span className="numero">{conDosDecimales(consulta.data.saldo)}</span>
        </p>
      </article>
    </div>
  );
}
